import React, { useMemo } from 'react';
import {
    createFavoriteQueue,
    deleteFavorite,
} from 'flow/actions/favorites-actions';
import { updateOwnQueueMemberLogin } from 'flow/actions/pbx-actions';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectFavoriteFromEntityKey,
    selectFavoriteKeyFromEntityKey,
    selectQueueFavorites,
} from 'flow/selectors/favorite-selectors';
import {
    makeSelectOwnUserIsLoggedInToQueue,
    makeSelectOwnUserIsMemberOfQueue,
    selectKeysWaitingQueueItems,
    selectQueueIsOfTypeCentionOmni,
} from 'flow/selectors/queue-selectors';
import { QueueKey } from 'flow/types/EntityKeys';
import PbxQueueMenuItem from './PbxQueueMenuItem';

interface Props {
    queueKey: QueueKey;
}

const PbxQueueContextMenu = ({ queueKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const itemsInQueue = useAppSelector((state) =>
        selectKeysWaitingQueueItems(state, queueKey),
    );
    const queueIsOmniType = useAppSelector((state) =>
        selectQueueIsOfTypeCentionOmni(state, queueKey),
    );

    const selectOwnUserIsMemberOfQueue = useMemo(
        makeSelectOwnUserIsMemberOfQueue,
        [],
    );
    const displayLoginItem = useAppSelector((state) =>
        selectOwnUserIsMemberOfQueue(state, queueKey),
    );

    const selectOwnUserIsLoggedInToQueue = useMemo(
        makeSelectOwnUserIsLoggedInToQueue,
        [],
    );
    const userIsLoggedInToQueue = useAppSelector((state) =>
        selectOwnUserIsLoggedInToQueue(state, queueKey),
    );

    const favorites = useAppSelector(selectQueueFavorites);
    const queueIsMarkedAsFavorite = !!selectFavoriteFromEntityKey(
        queueKey,
        favorites,
    );

    const onToggleFavorite = () => {
        if (!queueIsMarkedAsFavorite) {
            dispatch(createFavoriteQueue(queueKey));
        } else {
            const favoriteKey = selectFavoriteKeyFromEntityKey(
                queueKey,
                favorites,
            );
            dispatch(deleteFavorite(favoriteKey));
        }
    };

    const onToggleLogin = () => {
        dispatch(updateOwnQueueMemberLogin(queueKey, !userIsLoggedInToQueue));
    };

    return (
        <>
            <MenuItem onClick={onToggleFavorite}>
                {i18next.t('General.Favorite.Toggle.Title')}
                {queueIsMarkedAsFavorite && (
                    <div className="ms-6 leading-6">
                        <Icon iconKey="done" />
                    </div>
                )}
            </MenuItem>

            {displayLoginItem && (
                <MenuItem onClick={onToggleLogin}>
                    {userIsLoggedInToQueue
                        ? i18next.t('logout')
                        : i18next.t('login')}
                </MenuItem>
            )}

            {itemsInQueue?.size > 0 ? (
                <MenuItem className="font-bold text-gray-800" disabled={true}>
                    {queueIsOmniType
                        ? i18next.t('Queue.InQueue.Pick.Title')
                        : i18next.t('Queue.CallsInQueue.PickCall.Title')}
                </MenuItem>
            ) : (
                <MenuItem disabled={true}>
                    {queueIsOmniType
                        ? i18next.t('Queue.Omni.Section.Empty')
                        : i18next.t('Queue.CallsInQueue.Section.Empty')}
                </MenuItem>
            )}

            {itemsInQueue?.map((callKey, index) => {
                if (!callKey) {
                    return null;
                }

                return (
                    <PbxQueueMenuItem
                        key={callKey}
                        callKey={callKey}
                        queuePosition={index + 1}
                    />
                );
            })}
        </>
    );
};

export default PbxQueueContextMenu;
